import { FC, Fragment, useRef, useState, useEffect } from "react";
import { Col, Popconfirm, Row, Tooltip  } from "antd";
import { FormikProps, Form, FieldArray  } from "formik"
import { Prompt } from "react-router-dom";
import _ from "lodash";
import { InputWithLabel } from "../../molecules/InputWithLabel";
import {
  AccountStatusText,
  AccountStatusWrapper,
  ErrorMessage,
  StatusInfoText,
  StatusInfoWrapper,
  PopconfirmTitleWrapper,
  CreateSkillsLabel,
  CreateSkillsWrapper,
  SkillsDivider,
  RowWrapper,
  RemoveButton,
  RemoveButtonWrapper,
  InputTitle,
  AddSkillButton,
} from "./elements";
import { Switch } from "../../atoms/Switch";
import { exclamation, createSkills, plusOutlined } from "../../../assets/icons";

export type PositionType = {
  position: string;
  status: string;
  skills?: string[];
};

type Props = {
  popConfirmVisible?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  handleSwitchStatus: (checked: boolean) => void;
  innerRef?: React.Ref<FormikProps<any>>;
  isAccountActive: boolean;
  isSubmitting: boolean;
  position?: PositionType;
  errors?: { [field: string]: any };
  values: { position: string, skills: string[] };
  initialValues?: { position: string, skills: string[] };
  onChangePosition: (e: any) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
};

const PositionForm: FC<Props> = ({
  popConfirmVisible,
  onConfirm,
  onCancel,
  handleSwitchStatus,
  isAccountActive,
  isSubmitting,
  errors,
  values,
  onChangePosition,
  setFieldValue,
  initialValues = { position: "", status: "Closed", skills: [] },
}) => {
  const nodesRef = useRef<any>([]);
  const [isAdd, setIsAdd] = useState<boolean>(false);

  useEffect(() => {
    if (isAdd && values.skills.length > 0 && nodesRef.current) {
      nodesRef.current?.[values.skills.length - 1].focus();
    }
  }, [values.skills.length, isAdd]);

  return (
    <>
      <Prompt
        when={!_.isEqual(
          values,
          initialValues,
        ) && !isSubmitting}
        message="Are you sure you want to leave? Changes that you made may not be saved."
      />
      <Form>
        <RowWrapper>
          <Row gutter={[32, 0]}>
            <Col span={12}>
              <InputWithLabel
                error={(!!errors?.position) || false}
                value={values.position}
                onChange={onChangePosition}
                label="Position"
                type="default"
                name="position"
              />
              {errors?.position && (
                <ErrorMessage>{errors?.position}</ErrorMessage>
              )}
            </Col>
            <Col span={12}>
              <AccountStatusWrapper>
                <AccountStatusText>Status</AccountStatusText>
                <StatusInfoWrapper>
                  <Popconfirm
                    destroyTooltipOnHide={{ keepParent: false }}
                    getPopupContainer={(trigger: any) => trigger.parentElement}
                    placement="bottomRight"
                    visible={popConfirmVisible}
                    title={
                      <PopconfirmTitleWrapper>
                        <img src={exclamation} alt="project icon" />
                        <p>
                          Are you sure you want to update the position's status?
                        </p>
                      </PopconfirmTitleWrapper>
                    }
                    okText="Yes"
                    cancelText="Cancel"
                    cancelButtonProps={{
                      size: "middle",
                      danger: true,
                      type: "text",
                    }}
                    okButtonProps={{ size: "middle" }}
                    onCancel={onCancel}
                    onConfirm={onConfirm}
                  >
                    <Switch
                      checked={isAccountActive}
                      onChange={handleSwitchStatus}
                    />
                  </Popconfirm>
                  <StatusInfoText style={{ marginLeft: ".5em" }}>
                    {isAccountActive ? "Open" : "Closed"}
                  </StatusInfoText>
                </StatusInfoWrapper>
              </AccountStatusWrapper>
            </Col>
            <Col span={24}>
              <CreateSkillsWrapper>
                <CreateSkillsLabel>Create Skills</CreateSkillsLabel>
                <img src={createSkills} alt="create skills" />
              </CreateSkillsWrapper>
            </Col>
          </Row>
        </RowWrapper>
        <Row>
          <Col span={24}>
            <SkillsDivider />
          </Col>
        </Row>
        <RowWrapper>
          <Row gutter={[32, 16]}>
            <Col span={24} style={{ marginBottom: 8 }}>
              <InputTitle>Skill Name</InputTitle>
            </Col>
            <FieldArray
              name="skills"
              render={arrayHelpers => (
                <>
                  {values.skills && values.skills.length > 0 && (
                    values.skills.map((skill, index) => (
                      <Fragment key={index}>
                        <Col span={12}>
                          <InputWithLabel
                            error={(!!errors?.skills?.[index] || false)}
                            value={skill}
                            onChange={(e: any) => {
                              setFieldValue(`skills.${index}`, e.target?.value, true);
                            }}
                            label=""
                            type="default"
                            noMarginTop
                            name={`skills.${index}`}
                            maxLength={50}
                            inputRef={node => {
                              if (nodesRef?.current) (nodesRef!.current[index] = node); 
                            }}
                          />
                          {errors?.skills?.[index] && (
                            <ErrorMessage>{errors?.skills[index] }</ErrorMessage>
                          )}
                        </Col>
                        <Col span={12}>
                          <RemoveButtonWrapper>
                            <Tooltip
                              color="white"
                              placement="right"
                              title="Remove Skill" 
                              overlayInnerStyle={{
                                color: '#595959', 
                                paddingRight: 15, 
                                paddingLeft: 15,
                                fontStyle: 'normal',
                                fontWeight: 400,
                                fontSize: 14,
                              }}
                            >
                              <RemoveButton
                                type="button"
                                onClick={() => {
                                  arrayHelpers.remove(index);
                                  setIsAdd(false);
                                }}
                              >Remove</RemoveButton>
                            </Tooltip>
                          </RemoveButtonWrapper>
                        </Col>
                      </Fragment>
                    ))
                  )}
                  <Col span={24} style={{ marginBottom: 8 }}>
                    <AddSkillButton 
                      disabled={values.skills.length > 0 && errors?.skills}
                      type="button" onClick={() => {
                        arrayHelpers.push('');
                        setIsAdd(true);
                      }}
                    >
                      <img src={plusOutlined} alt="add skills" />
                      <span>Add Another Skill</span>
                    </AddSkillButton>
                  </Col>
                </>
              )}
            />
            
          </Row>
        </RowWrapper>
      </Form>
    </>
  );
};

export default PositionForm;
